import { useEffect, useMemo, useRef, useState } from "react";
import { Upload, Copy, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatApiError } from "@/lib/api";

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/media").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load media")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(
    () => items.filter((m) => m.filename.toLowerCase().includes(q.toLowerCase())),
    [items, q]
  );

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((prev) => [...data, ...prev]);
      toast.success(`${data.length} file(s) uploaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyUrl = (m) => {
    navigator.clipboard.writeText(mediaUrl(m.url)).then(
      () => toast.success("URL copied"),
      () => toast.error("Could not copy")
    );
  };

  const remove = async (m) => {
    try {
      await api.delete(`/admin/media/${m.id}`);
      setItems((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Media deleted");
    } catch {
      toast.error("Could not delete media");
    }
  };

  return (
    <div data-testid="admin-media-page">
      <div className="mb-8">
        <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Media Library</h1>
        <p className="mt-1 text-sm text-neutral-500">{items.length} files — reuse them across portfolio and blog</p>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
        className={`mb-6 flex flex-col items-center gap-3 rounded-3xl border border-dashed p-10 text-center transition-colors ${
          dragging ? "border-white/50 bg-white/[0.05]" : "border-white/15 bg-white/[0.02]"
        }`}
        data-testid="media-dropzone"
      >
        <Upload className="h-6 w-6 text-neutral-500" />
        <p className="text-sm text-neutral-400">Drag & drop images or videos here</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">JPG · PNG · WebP · SVG · MP4 · WebM</p>
        <button onClick={() => inputRef.current?.click()} className="btn-ghost !py-2.5 text-xs" data-testid="media-upload-button">
          Browse files
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,video/mp4,video/webm,.svg"
          className="hidden"
          data-testid="media-file-input"
          onChange={(e) => upload(e.target.files)}
        />
      </div>

      <div className="relative mb-6 max-w-xs">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search media…"
          className="input-glass !pl-11 !py-2.5"
          data-testid="media-search-input"
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => <div key={i} className="aspect-square animate-pulse rounded-2xl bg-white/[0.04]" />)}
        </div>
      ) : filtered.length === 0 ? (
        <p className="py-16 text-center text-sm text-neutral-500" data-testid="media-empty">
          {items.length === 0 ? "No media uploaded yet." : "No files match your search."}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-5" data-testid="media-grid">
          {filtered.map((m) => (
            <div key={m.id} className="group glass overflow-hidden rounded-2xl" data-testid={`media-item-${m.id}`}>
              <div className="relative aspect-square overflow-hidden">
                {m.type === "video" ? (
                  <video src={mediaUrl(m.url)} muted preload="metadata" className="h-full w-full object-cover" />
                ) : (
                  <img src={mediaUrl(m.url)} alt={m.filename} loading="lazy" className="h-full w-full object-cover" />
                )}
                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                  <button onClick={() => copyUrl(m)} aria-label="Copy URL" data-testid={`media-copy-${m.id}`} className="glass-strong flex h-9 w-9 items-center justify-center rounded-full text-white hover:text-neutral-300">
                    <Copy className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(m)} aria-label="Delete" data-testid={`media-delete-${m.id}`} className="glass-strong flex h-9 w-9 items-center justify-center rounded-full text-white hover:text-red-300">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <p className="truncate text-xs font-medium text-white">{m.filename}</p>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                  {m.type} · {Math.round((m.size || 0) / 1024)} KB
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
